/**
 * Panel de arqueo de una caja cerrada.
 *
 * El cierre lo hace el recepcionista y termina ahí: un botón, y se va. El
 * arqueo lo hace después un encargado desde Historial de Cajas.
 *
 * Qué se declara:
 *   - EFECTIVO: se cuenta físicamente, una fila por moneda. Es obligatorio.
 *   - RESTO (tarjeta, transferencia, Zelle): no se cuenta, se confirma contra
 *     el lote del punto de venta y el estado de cuenta. Viene prellenado con lo
 *     esperado y se corrige solo si el soporte dice otra cosa.
 *
 * Si hay diferencia el arqueo se registra igual, pero exige un motivo: una caja
 * tiene que poder cerrarse, y la diferencia es información, no un bloqueo.
 *
 * DISEÑO — esta es la única sección de la pantalla que usa color, porque es la
 * única que tiene estado: verde cuando cuadra, rojo cuando no. El resto del
 * reporte es neutro a propósito, así el ojo va directo acá. Los fondos se arman
 * con `alpha()` sobre los colores del tema y no con tokens tipo `success.lighter`,
 * que esta app no define y MUI no resuelve.
 */

import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HistoryIcon from '@mui/icons-material/History';
import { toast } from 'react-toastify';
import { getApi, postApi } from 'views/services/api';
import { methodLabel, money, numeric } from './cashShared';

// Rótulo de paso: un número en un círculo + el título de la sección. Convierte
// el arqueo en una secuencia clara (1 contá, 2 verificá, 3 cerrá) en vez de tres
// bloques sueltos.
const StepTitle = ({ step, title, hint }) => (
  <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ mb: 1.5 }}>
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 700,
        flexShrink: 0,
        mt: '1px'
      }}
    >
      {step}
    </Box>
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Box>
  </Stack>
);

StepTitle.propTypes = { step: PropTypes.node, title: PropTypes.node, hint: PropTypes.node };

// Métodos que llevan lote del datáfono además del número de transacción.
const METHODS_WITH_BATCH = new Set(['card']);

/** Chip de referencia del soporte: nombre + valor, o aviso en rojo si falta. */
const RefTag = ({ label, value }) => {
  const missing = !value;
  return (
    <Chip
      size="small"
      variant="outlined"
      label={
        <Box component="span" sx={numeric}>
          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            {label}
          </Box>{' '}
          {missing ? 'sin registrar' : value}
        </Box>
      }
      sx={{
        height: 22,
        borderColor: missing ? 'warning.main' : 'divider',
        color: missing ? 'warning.dark' : 'text.primary',
        '& .MuiChip-label': { px: 0.75 }
      }}
    />
  );
};

RefTag.propTypes = { label: PropTypes.node, value: PropTypes.string };

const CashAuditPanel = ({ registerId, onAudited }) => {
  const theme = useTheme();
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Lo que el encargado declara. Las claves son moneda (efectivo) y
  // método_moneda (el resto), igual que las arma el servidor.
  const [counted, setCounted] = useState({});
  const [declared, setDeclared] = useState({});
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  // Escape para cuadrar a mano una caja marcada como anterior al arqueo.
  const [forceLegacyForm, setForceLegacyForm] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await getApi(`api/cashregister/audit-sheet/${registerId}`);
        if (cancelled) return;

        if (!r?.data) {
          setError('No se pudo cargar la planilla de arqueo.');
          return;
        }

        setSheet(r.data);

        // Los métodos electrónicos arrancan en lo esperado: lo normal es que
        // el soporte coincida y el encargado solo confirme.
        const seed = {};
        (r.data.methods || []).forEach((m) => {
          seed[`${m.method}_${m.currency}`] = String(m.expected);
        });
        setDeclared(seed);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || 'No se pudo cargar la planilla de arqueo.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [registerId]);

  const cashRows = sheet?.cash || [];
  const methodRows = sheet?.methods || [];
  const baseCurrency = sheet?.baseCurrency || 'USD';

  /**
   * Diferencia total en moneda base, en vivo.
   *
   * Cada fila se convierte con su propia tasa implícita (lo que se cobró en
   * base dividido lo que se cobró en su moneda), que es la que se aplicó al
   * momento del pago. Usar la tasa de hoy inventaría un descuadre cambiario.
   */
  const rowDifferenceInBase = (row, declaredValue) => {
    const difference = Number(declaredValue || 0) - Number(row.expected || 0);
    if (!row.expected || !row.expectedInBase) return difference;
    return (difference * row.expectedInBase) / row.expected;
  };

  const totalDifference = [
    ...cashRows.map((row) => rowDifferenceInBase(row, counted[row.currency])),
    ...methodRows.map((row) => rowDifferenceInBase(row, declared[`${row.method}_${row.currency}`]))
  ].reduce((acc, d) => acc + (Number.isFinite(d) ? d : 0), 0);

  const roundedDifference = Number(totalDifference.toFixed(2));
  const hasDifference = Math.abs(roundedDifference) >= 0.01;
  const allCashCounted = cashRows.every((row) => counted[row.currency] !== undefined && counted[row.currency] !== '');
  const canSubmit = allCashCounted && (!hasDifference || reason.trim().length > 0);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        countedCash: cashRows.map((row) => ({
          currency: row.currency,
          counted: Number(counted[row.currency] || 0)
        })),
        methodTotals: methodRows.map((row) => ({
          method: row.method,
          currency: row.currency,
          declared: Number(declared[`${row.method}_${row.currency}`] ?? row.expected),
          confirmed: true
        })),
        reason: reason.trim(),
        notes: notes.trim()
      };

      const r = await postApi(`api/cashregister/audit/${registerId}`, payload);

      if (r?.status === 200) {
        toast.success(r.data.message);
        if (onAudited) onAudited();
      } else {
        toast.error(r?.response?.data?.error || 'No se pudo registrar el arqueo.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo registrar el arqueo.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" gap={2} py={1}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Cargando la planilla de arqueo…
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  // ── Caja ya arqueada: el acta, no el formulario ──────────────────────
  if (sheet?.auditStatus === 'audited' && sheet?.audit) {
    const { audit } = sheet;
    const difference = Number(audit.totalDifferenceBase || 0);
    const balanced = Math.abs(difference) < 0.01;
    const tone = balanced ? theme.palette.success.dark : theme.palette.error.main;

    return (
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2, borderColor: alpha(tone, 0.4), bgcolor: alpha(tone, 0.03) }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
            <FactCheckIcon fontSize="small" sx={{ color: tone }} />
            <Typography variant="subtitle1" fontWeight={700}>
              Arqueo registrado
            </Typography>
            <Chip
              size="small"
              label={balanced ? 'Cuadra' : `Diferencia ${difference.toFixed(2)} ${baseCurrency}`}
              sx={{ bgcolor: alpha(tone, 0.12), color: tone, fontWeight: 700, ...numeric }}
            />
            {audit.selfAudited && <Chip size="small" variant="outlined" label="Firmada por quien la cerró" />}
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Concepto</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    Esperado
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    Contado
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    Diferencia
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(audit.countedCash || []).map((row) => (
                  <TableRow key={`cash-${row.currency}`}>
                    <TableCell>
                      Efectivo {row.currency}
                      {(row.float > 0 || row.collected > 0) && (
                        <Typography variant="caption" color="text.disabled" display="block" sx={numeric}>
                          fondo {money(row.float, row.currency)} + cobrado {money(row.collected, row.currency)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={numeric}>
                      {money(row.expected, row.currency)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...numeric, fontWeight: 600 }}>
                      {money(row.counted, row.currency)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...numeric, color: row.difference === 0 ? 'text.disabled' : 'error.main', fontWeight: 600 }}
                    >
                      {row.difference.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
                {(audit.methodTotals || []).map((row) => (
                  <TableRow key={`m-${row.method}-${row.currency}`}>
                    <TableCell>
                      {methodLabel(row.method)} {row.currency}
                    </TableCell>
                    <TableCell align="right" sx={numeric}>
                      {money(row.expected, row.currency)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...numeric, fontWeight: 600 }}>
                      {money(row.declared, row.currency)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...numeric, color: row.difference === 0 ? 'text.disabled' : 'error.main', fontWeight: 600 }}
                    >
                      {row.difference.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {audit.reason && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>Motivo de la diferencia:</strong> {audit.reason}
            </Alert>
          )}
          {audit.notes && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {audit.notes}
            </Typography>
          )}
          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 2 }}>
            Arqueada el {new Date(audit.auditedAt).toLocaleString('es-ES')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // ── Caja anterior al arqueo ──────────────────────────────────────────
  // No se ofrece el formulario de entrada: nadie contó ese efectivo y ya no hay
  // cómo saberlo, así que cuadrarla hoy sería inventar el conteo. Queda el
  // escape explícito para quien tenga el papel del turno a mano.
  if (sheet?.auditStatus === 'legacy' && !forceLegacyForm) {
    return (
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1 }}>
            <HistoryIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            <Typography variant="subtitle1" fontWeight={700} color="text.secondary">
              Caja anterior al arqueo
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '62ch' }}>
            Este turno se cerró antes de que el sistema tuviera arqueo. El efectivo nunca se contó, así que no hay forma de saber hoy si
            cuadraba. El total de arriba es lo que registró el sistema, no lo que había en la gaveta.
          </Typography>
          <Button size="small" variant="text" sx={{ mt: 1.5, ml: -1 }} onClick={() => setForceLegacyForm(true)}>
            Cuadrar de todas formas
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ── Formulario de arqueo ─────────────────────────────────────────────
  // Antes de contar el efectivo no hay un resultado real: el conteo vacío cuenta
  // como cero y daría un "faltante" alarmante que no significa nada. Hasta
  // entonces el recuadro queda neutro.
  const pendingCount = cashRows.length > 0 && !allCashCounted;
  const resultTone = pendingCount ? theme.palette.text.secondary : hasDifference ? theme.palette.error.main : theme.palette.success.dark;

  return (
    <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <FactCheckIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" fontWeight={700}>
            Arqueo de la caja #{sheet?.registerNumber}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, maxWidth: '68ch' }}>
          Tres pasos: contá el efectivo de la gaveta, verificá cada pago electrónico contra su soporte y cerrá el arqueo. Si algo no cuadra
          se registra igual, solo hay que anotar el motivo.
        </Typography>

        {sheet?.isFrozen === false && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Esta caja se cerró antes de que el sistema guardara el corte, así que los montos esperados se recalcularon ahora y podrían no
            coincidir con lo que se firmó ese día.
          </Alert>
        )}

        <Divider sx={{ my: 2.5 }} />

        {/* Efectivo — lo único que se cuenta */}
        <StepTitle
          step={1}
          title="Contá el efectivo de la gaveta"
          hint="Una moneda por tarjeta. “Debe haber” es el fondo con el que se abrió más lo que se cobró en el turno: todo eso está físicamente en la gaveta."
        />

        {cashRows.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1, mb: 3 }}>
            No hubo efectivo en este turno.
          </Typography>
        ) : (
          <Grid container spacing={2} sx={{ mt: 0, mb: 3 }}>
            {cashRows.map((row) => {
              const value = counted[row.currency];
              const hasCount = value !== undefined && value !== '';
              const difference = hasCount ? Number(value) - row.expected : null;
              const balanced = difference !== null && Math.abs(difference) < 0.01;
              return (
                <Grid item xs={12} md={6} key={row.currency}>
                  <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 1.25 }}>
                      {row.currency}
                    </Typography>

                    {/* De dónde sale lo esperado. Sin esto el recepcionista ve
                        un número solo y no sabe si incluye el fondo. */}
                    <Stack sx={{ mb: 1.5 }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                          Fondo de apertura
                        </Typography>
                        <Typography variant="caption" sx={numeric} color="text.secondary">
                          {money(row.float, row.currency)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                          Cobrado en el turno
                        </Typography>
                        <Typography variant="caption" sx={numeric} color="text.secondary">
                          {money(row.collected, row.currency)}
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}
                      >
                        <Typography variant="body2" fontWeight={700}>
                          Debe haber
                        </Typography>
                        <Typography variant="body2" fontWeight={700} sx={numeric}>
                          {money(row.expected, row.currency)}
                        </Typography>
                      </Stack>
                    </Stack>

                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Contado en la gaveta"
                      value={value ?? ''}
                      onChange={(e) => setCounted({ ...counted, [row.currency]: e.target.value })}
                      inputProps={{ step: '0.01', style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }}
                    />

                    {difference !== null && (
                      <Stack sx={{ mt: 1 }} spacing={0.25}>
                        <Typography variant="caption" sx={numeric} color={balanced ? 'success.dark' : 'error.main'}>
                          {balanced
                            ? 'Cuadra'
                            : `${difference > 0 ? 'Sobrante' : 'Faltante'} de ${Math.abs(difference).toFixed(2)} ${row.currency}`}
                        </Typography>
                        {/* El fondo se entrega junto con lo cobrado: el turno
                            siguiente saca el suyo de la caja fuerte. */}
                        <Typography variant="caption" color="text.secondary" sx={numeric}>
                          A entregar a la caja fuerte: <strong>{money(Number(value), row.currency)}</strong>
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Métodos electrónicos — se confirman contra el soporte, no se cuentan */}
        <StepTitle
          step={2}
          title="Verificá los pagos electrónicos"
          hint="Tildá cada cobro contra el lote del datáfono y el estado de cuenta. Corregí el monto solo si el soporte dice otra cosa."
        />

        {methodRows.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1, mb: 3 }}>
            No hubo cobros electrónicos en este turno.
          </Typography>
        ) : (
          <Stack spacing={2} sx={{ mb: 3 }}>
            {methodRows.map((row) => {
              const key = `${row.method}_${row.currency}`;
              const value = declared[key];
              const difference = Number(value || 0) - row.expected;
              const balanced = Math.abs(difference) < 0.01;
              const charges = row.charges || [];
              const needsBatch = METHODS_WITH_BATCH.has(String(row.method).toLowerCase());

              return (
                <Box key={key} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={700}>
                        {methodLabel(row.method)}
                      </Typography>
                      <Chip size="small" variant="outlined" label={row.currency} />
                      <Typography variant="caption" color="text.secondary">
                        {charges.length} {charges.length === 1 ? 'cobro' : 'cobros'}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={numeric}>
                      Esperado <strong>{money(row.expected, row.currency)}</strong>
                    </Typography>
                  </Stack>

                  {/* Soporte de cada cobro: lo que el encargado coteja uno por uno */}
                  {charges.length > 0 ? (
                    <Stack sx={{ mt: 1.5 }} divider={<Divider flexItem sx={{ borderStyle: 'dashed' }} />} spacing={1}>
                      {charges.map((c, i) => (
                        <Stack
                          key={`${key}-${i}`}
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          flexWrap="wrap"
                          gap={1}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="body2" fontWeight={600}>
                              {c.roomNo ? `Hab. ${c.roomNo}` : 'Cobro'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={numeric}>
                              {money(c.amount, c.currency)}
                            </Typography>
                            {needsBatch && <RefTag label="Lote" value={c.batchNumber} />}
                            <RefTag label="Transacción" value={c.transactionNumber} />
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                      Este cobro no guardó lote ni número de transacción. Cotejalo contra el estado de cuenta.
                    </Typography>
                  )}

                  {/* Confirmación contra el soporte */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1.5}
                    sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
                  >
                    <TextField
                      size="small"
                      type="number"
                      label="Según el estado de cuenta"
                      value={value ?? ''}
                      onChange={(e) => setDeclared({ ...declared, [key]: e.target.value })}
                      inputProps={{ step: '0.01', style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }}
                      sx={{ width: 190 }}
                    />
                    <Typography variant="body2" sx={{ ...numeric, fontWeight: 600, color: balanced ? 'success.dark' : 'error.main' }}>
                      {balanced
                        ? 'Coincide con el soporte'
                        : `${difference > 0 ? 'Sobrante' : 'Faltante'} de ${Math.abs(difference).toFixed(2)} ${row.currency}`}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}

        {/* Resultado — el único lugar con color */}
        <StepTitle step={3} title="Resultado del arqueo" />
        <Box
          sx={{
            p: 2,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: alpha(resultTone, 0.35),
            bgcolor: alpha(resultTone, 0.06),
            mb: 2
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="body2" fontWeight={700}>
                Diferencia total
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {pendingCount
                  ? 'Contá el efectivo para ver si la caja cuadra'
                  : hasDifference
                  ? `${roundedDifference < 0 ? 'Faltante' : 'Sobrante'} respecto de lo que registró el sistema`
                  : 'El conteo coincide con lo registrado'}
              </Typography>
            </Box>
            <Typography variant="h3" fontWeight={700} sx={{ ...numeric, color: resultTone }}>
              {pendingCount ? '—' : roundedDifference.toFixed(2)}
            </Typography>
          </Stack>
        </Box>

        {hasDifference && (
          <TextField
            fullWidth
            required
            size="small"
            label="Motivo de la diferencia"
            placeholder="Ej. vuelto mal dado en la habitación 204"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ mb: 2 }}
            helperText="Obligatorio cuando la caja no cuadra."
          />
        )}

        <TextField
          fullWidth
          multiline
          rows={2}
          size="small"
          label="Observaciones (opcional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mb: 2 }}
        />

        {!allCashCounted && cashRows.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Falta declarar el conteo de efectivo para poder registrar el arqueo.
          </Alert>
        )}

        <Button
          variant="contained"
          disableElevation
          size="large"
          fullWidth
          disabled={!canSubmit || saving}
          onClick={handleSubmit}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <FactCheckIcon />}
        >
          {saving ? 'Registrando…' : 'Registrar arqueo'}
        </Button>
      </CardContent>
    </Card>
  );
};

CashAuditPanel.propTypes = {
  registerId: PropTypes.string.isRequired,
  onAudited: PropTypes.func
};

export default CashAuditPanel;
